/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/comps/PricePermissionProvider.tsx
 * @Description: 商品价格显示权限 Provider
 *
 * 在客户端获取商品价格显示权限，并通过 Context 提供给子组件
 * 用于控制商品列表、商品详情等页面是否显示价格
 */

import { ReactNode } from 'react';
import { PriceContext } from 'libs/contexts';
import { usePricePermissionQuery } from 'hooks/usePricePermissionQuery';

export interface PricePermissionProviderProps {
  children: ReactNode;
  /**
   * 初始数据（可选）
   * 用于服务端渲染场景，如果提供则使用初始数据，避免客户端重新请求
   */
  initialData?: PriceContextValue;
}

/**
 * 商品价格显示权限 Provider 组件
 *
 * 功能：
 * 1. 使用 React Query 在客户端获取价格显示权限
 * 2. 通过 PriceContext 提供数据给子组件
 * 3. 自动处理 loading 和 error 状态
 * 4. 支持初始数据，避免重复请求
 * 5. 默认值：不显示价格（安全策略）
 *
 * @example
 * ```tsx
 * // 在 _app.tsx 中使用
 * <PricePermissionProvider>
 *   <YourApp />
 * </PricePermissionProvider>
 *
 * // 在子组件中使用
 * function ProductCard({ product }) {
 *   const priceContext = useContext(PriceContext);
 *   return (
 *     <div>
 *       {priceContext.showPrice ? <span>¥{product.price}</span> : <span>请登录查看价格</span>}
 *     </div>
 *   );
 * }
 * ```
 */
export default function PricePermissionProvider({
  children,
  initialData,
}: PricePermissionProviderProps) {
  // 使用 React Query 获取价格显示权限
  const {
    data: pricePermission,
    isLoading,
    isError,
    error,
  } = usePricePermissionQuery({
    initialData,
  });

  // 错误处理：如果加载失败，使用默认值（不显示价格）
  if (isError) {
    console.error('加载价格权限失败:', error);
  }

  // 提供数据给子组件
  // 注意：默认值为不显示价格，保证安全性
  const defaultValue: PriceContextValue = {
    showPrice: false,
    tip: '',
    showSellCount: false,
    showScore: false,
  };

  const priceData =
    isLoading || isError ? initialData || defaultValue : pricePermission || defaultValue;

  return <PriceContext.Provider value={priceData}>{children}</PriceContext.Provider>;
}
